import { 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc, 
  serverTimestamp,
  Timestamp,
  updateDoc,
  arrayUnion,
} from 'firebase/firestore';
import { db } from './firebase';

// Collection names
const COLLECTIONS = {
  GRIDS: 'reactgrids',
  USER_SETTINGS: 'userSettings',
  TEMPLATES: 'templates',
  USER_TEMPLATES: 'userTemplates',
  TEMPLATE_DATA: 'templateData', // New collection for individual template data
  SUBSCRIPTIONS: 'subscriptions', // Subscription data collection
} as const;

// Interfaces for ReactGrid data
export interface GridMetadata {
  id: string;
  title: string;
  created: number;
  lastModified: number;
  userId: string;
}

export interface GridData {
  id: string;
  title: string;
  gridData: any[]; // ReactGrid row data
  columnConfigs: any[]; // Column configurations
  lastModified: number;
  created: number;
  userId: string;
}

export interface TemplateData extends GridData {
  description?: string;
  category?: string;
  isPublic?: boolean;
}

export interface SubscriptionData {
  userId: string;
  plan: string;
  credits: number;
  maxCredits: number;
  creditsResetDate: number | null;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
  billingCycle: string | null;
  subscriptionId: string | null;
  lifetime?: boolean;
}

// Interface for tracking cell changes
export interface CellChange {
  rowIndex: number;
  columnId: string;
  newValue: any;
  oldValue?: any;
}

// Interface for tracking row changes
export interface RowChange {
  rowIndex: number;
  row: any;
  changeType: 'update' | 'insert' | 'delete';
}

/**
 * Clean data for Firestore storage by removing functions and non-serializable values
 */
const cleanDataForFirestore = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }
  
  if (typeof data === 'function' || typeof data === 'symbol') {
    return undefined;
  }
  
  if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
    return data;
  }
  
  if (Array.isArray(data)) {
    return data.map(item => cleanDataForFirestore(item)).filter(item => item !== undefined);
  }
  
  if (typeof data === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(data)) {
      const cleanedValue = cleanDataForFirestore(value);
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }
    }
    return cleaned;
  }
  
  return undefined;
};

/**
 * Convert gridData array to map format for Firestore storage
 */
const convertGridDataToMap = (gridDataArray: any[]): Record<string, any> => {
  const gridDataMap: Record<string, any> = {};
  gridDataArray.forEach((row, index) => {
    gridDataMap[index.toString()] = row;
  });
  return gridDataMap;
};

/**
 * Convert gridData map to array format for application use
 */
const convertGridDataToArray = (gridDataMap: Record<string, any>): any[] => {
  if (Array.isArray(gridDataMap)) {
    // Already an array (backward compatibility)
    return gridDataMap;
  }
  
  const gridDataArray: any[] = [];
  const keys = Object.keys(gridDataMap).sort((a, b) => parseInt(a) - parseInt(b));
  
  keys.forEach(key => {
    gridDataArray.push(gridDataMap[key]);
  });
  
  return gridDataArray;
};

/**
 * Save a grid to Firestore
 * /reactgrids/{userId} - contains metadata for sidebar
 * /reactgrids/{userId}/grids/{gridId} - individual grid data
 */
export const saveGridToFirestore = async (gridData: GridData): Promise<void> => {
  try {
    // Validate that userId is defined and not empty
    if (!gridData.userId || gridData.userId.trim() === '') {
      throw new Error('Invalid userId: userId is required and cannot be empty');
    }

    const userDocRef = doc(db, COLLECTIONS.GRIDS, gridData.userId);
    const gridRef = doc(userDocRef, 'grids', gridData.id);
    
    // Convert gridData array to map format for storage
    const gridDataMap = convertGridDataToMap(gridData.gridData);
    
    // Clean the data before saving
    const cleanedGridData = cleanDataForFirestore({
      ...gridData,
      gridData: gridDataMap
    });
    
    // Save the full grid data in the subcollection
    const gridDataForStorage = {
      ...cleanedGridData,
      lastModified: serverTimestamp(),
      created: gridData.created ? Timestamp.fromMillis(gridData.created) : serverTimestamp()
    };
    
    await setDoc(gridRef, gridDataForStorage);
    
    // Update the parent document with metadata for fast sidebar loading
    const metadata: GridMetadata = {
      id: gridData.id,
      title: gridData.title,
      created: gridData.created,
      lastModified: Date.now(),
      userId: gridData.userId
    };
    
    // Get current user document to check if it exists
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      const existingGrids = userData.grids || [];
      
      // Update existing grid or add new one
      const existingIndex = existingGrids.findIndex((g: any) => g.id === gridData.id);
      
      if (existingIndex >= 0) {
        // Update existing grid metadata
        existingGrids[existingIndex] = metadata;
        await updateDoc(userDocRef, {
          grids: existingGrids,
          lastUpdated: serverTimestamp()
        });
      } else {
        // Add new grid metadata
        await updateDoc(userDocRef, {
          grids: arrayUnion(metadata),
          lastUpdated: serverTimestamp()
        });
      }
    } else {
      // Create new user document with first grid
      await setDoc(userDocRef, {
        userId: gridData.userId,
        grids: [metadata],
        lastUpdated: serverTimestamp(),
        created: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error saving grid to Firestore:', error);
    throw error;
  }
};

/**
 * Get all grids metadata for a specific user
 */
export const getUserGridsFromFirestore = async (userId: string): Promise<GridMetadata[]> => {
  try {
    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      const grids = userData.grids || [];
      
      // Sort by lastModified in descending order (most recent first)
      return grids.sort((a: GridMetadata, b: GridMetadata) => 
        b.lastModified - a.lastModified
      );
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error getting user grids from Firestore:', error);
    throw error;
  }
};

/**
 * Get a specific grid from Firestore
 */
export const getGridFromFirestore = async (id: string, userId: string): Promise<GridData | null> => {
  try {
    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    const gridRef = doc(userDocRef, 'grids', id);
    const docSnap = await getDoc(gridRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Convert gridData from map back to array format
      const gridDataArray = convertGridDataToArray(data.gridData || {});
      
      return {
        id: docSnap.id,
        title: data.title,
        gridData: gridDataArray,
        columnConfigs: data.columnConfigs || [],
        created: data.created?.toMillis() || Date.now(),
        lastModified: data.lastModified?.toMillis() || Date.now(),
        userId: data.userId
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Error getting grid ${id} from Firestore:`, error);
    throw error;
  }
};

/**
 * Get minimal grid data for preview (only first 8 rows and 5 columns)
 */
export const getGridPreviewFromFirestore = async (id: string, userId: string): Promise<GridData | null> => {
  try {
    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    const gridRef = doc(userDocRef, 'grids', id);
    const docSnap = await getDoc(gridRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Convert gridData from map back to array format
      const fullGridDataArray = convertGridDataToArray(data.gridData || {});
      const fullColumnConfigs = data.columnConfigs || [];
      
      // Limit to first 8 rows and 5 columns for preview
      const previewGridData = fullGridDataArray.slice(0, 8).map((row: any) => {
        const previewRow: any = { id: row.id };
        
        // Only include data for first 5 columns
        fullColumnConfigs.slice(0, 5).forEach((col: any) => {
          previewRow[col.id] = row[col.id] || '';
        });
        
        return previewRow;
      });
      
      return {
        id: docSnap.id,
        title: data.title,
        gridData: previewGridData,
        columnConfigs: fullColumnConfigs.slice(0, 5), // Only first 5 columns
        created: data.created?.toMillis() || Date.now(),
        lastModified: data.lastModified?.toMillis() || Date.now(),
        userId: data.userId
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error(`Error getting grid preview ${id} from Firestore:`, error);
    throw error;
  }
};

/**
 * Delete a grid from Firestore
 */
export const deleteGridFromFirestore = async (id: string, userId: string): Promise<void> => {
  try {
    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    const gridRef = doc(userDocRef, 'grids', id);
    
    // Get the current user document
    const userDocSnap = await getDoc(userDocRef);
    if (!userDocSnap.exists()) {
      throw new Error(`User document ${userId} not found`);
    }
    
    const userData = userDocSnap.data();
    const grids = userData.grids || [];
    
    // Check if grid exists in the metadata
    const gridExists = grids.some((g: any) => g.id === id);
    
    if (!gridExists) {
      console.warn(`Grid ${id} not found in user metadata, but will still try to delete subcollection`);
    }
    
    // Delete from subcollection first
    await deleteDoc(gridRef);
    
    // Remove from parent document metadata only if it exists
    if (gridExists) {
      const updatedGrids = grids.filter((g: any) => g.id !== id);
      
      await updateDoc(userDocRef, {
        grids: updatedGrids,
        lastUpdated: serverTimestamp()
      });
      
    }
    
  } catch (error) {
    console.error(`Error deleting grid ${id} from Firestore:`, error);
    
    // If it's a "not found" error, that's actually success for deletion
    if (error && typeof error === 'object' && 'code' in error && error.code === 'not-found') {
      return;
    }
    
    throw error;
  }
};

/**
 * Clean up orphaned grids from user metadata (grids that exist in metadata but not in subcollection)
 */
export const cleanupOrphanedGrids = async (userId: string): Promise<void> => {
  try {
    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    
    // Get user document with metadata
    const userDocSnap = await getDoc(userDocRef);
    if (!userDocSnap.exists()) {
      return;
    }
    
    const userData = userDocSnap.data();
    const grids = userData.grids || [];
    
    // Check each grid to see if it actually exists in the subcollection
    const validGrids = [];
    
    for (const grid of grids) {
      try {
        const gridRef = doc(userDocRef, 'grids', grid.id);
        const gridSnap = await getDoc(gridRef);
        
        if (gridSnap.exists()) {
          validGrids.push(grid);
        }
      } catch (error) {
        console.error(`Error checking grid ${grid.id}:`, error);
        // If there's an error checking, assume it's orphaned
      }
    }
    
    if (validGrids.length !== grids.length) {
      // Update the user document with only valid grids
      await updateDoc(userDocRef, {
        grids: validGrids,
        lastUpdated: serverTimestamp()
      });
    }
    
  } catch (error) {
    console.error(`Error cleaning up orphaned grids for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Save user settings to Firestore
 */
export const saveUserSettingsToFirestore = async (userId: string, settings: any): Promise<void> => {
  try {
    const userSettingsRef = doc(db, COLLECTIONS.USER_SETTINGS, userId);
    await setDoc(userSettingsRef, {
      ...settings,
      lastUpdated: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving user settings to Firestore:', error);
    throw error;
  }
};

/**
 * Get user settings from Firestore
 */
export const getUserSettingsFromFirestore = async (userId: string): Promise<any> => {
  try {
    const userSettingsRef = doc(db, COLLECTIONS.USER_SETTINGS, userId);
    const docSnap = await getDoc(userSettingsRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return {};
    }
  } catch (error) {
    console.error('Error getting user settings from Firestore:', error);
    return {};
  }
};

/**
 * Update just the title of a specific grid
 */
export const updateGridTitle = async (id: string, userId: string, newTitle: string): Promise<void> => {
  try {
    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    const gridRef = doc(userDocRef, 'grids', id);
    
    // Update the title in the subcollection
    await updateDoc(gridRef, {
      title: newTitle,
      lastModified: serverTimestamp()
    });
    
    // Update the metadata in the parent document
    const userDocSnap = await getDoc(userDocRef);
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      const grids = userData.grids || [];
      
      // Find and update the grid metadata
      const existingIndex = grids.findIndex((g: any) => g.id === id);
      
      if (existingIndex >= 0) {
        grids[existingIndex] = {
          ...grids[existingIndex],
          title: newTitle,
          lastModified: Date.now()
        };
        
        await updateDoc(userDocRef, {
          grids: grids,
          lastUpdated: serverTimestamp()
        });
        
      }
    }
  } catch (error) {
    console.error(`Error updating grid title for ${id}:`, error);
    throw error;
  }
};

/**
 * Get all general templates from Firestore (optimized - metadata only)
 */
export const getGeneralTemplatesFromFirestore = async (): Promise<TemplateData[]> => {
  try {
    const templatesRef = doc(db, COLLECTIONS.TEMPLATES, 'public');
    const templatesSnap = await getDoc(templatesRef);
    
    if (templatesSnap.exists()) {
      const data = templatesSnap.data();
      const templates = (data.templates || []).map((template: any) => ({
        id: template.id,
        title: template.title,
        description: template.description,
        category: template.category,
        isPublic: template.isPublic,
        created: template.created,
        lastModified: template.lastModified,
        userId: template.userId,
        // Don't include gridData and columnConfigs in list view
        gridData: [],
        columnConfigs: []
      }));
      
      // Sort by lastModified (most recent first), then by created
      return templates.sort((a: TemplateData, b: TemplateData) => {
        const aModified = a.lastModified || a.created || 0;
        const bModified = b.lastModified || b.created || 0;
        
        if (aModified !== bModified) {
          return bModified - aModified; // Descending order (newest first)
        }
        
        const aCreated = a.created || 0;
        const bCreated = b.created || 0;
        return bCreated - aCreated; // Descending order (newest first)
      });
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error getting general templates from Firestore:', error);
    return [];
  }
};

/**
 * Get user's personal templates from Firestore (optimized - metadata only)
 */
export const getUserTemplatesFromFirestore = async (userId: string): Promise<TemplateData[]> => {
  try {
    const userTemplatesRef = doc(db, COLLECTIONS.USER_TEMPLATES, userId);
    const userTemplatesSnap = await getDoc(userTemplatesRef);
    
    if (userTemplatesSnap.exists()) {
      const data = userTemplatesSnap.data();
      const templates = (data.templates || []).map((template: any) => ({
        id: template.id,
        title: template.title,
        description: template.description,
        category: template.category,
        isPublic: template.isPublic,
        created: template.created,
        lastModified: template.lastModified,
        userId: template.userId,
        // Don't include gridData and columnConfigs in list view
        gridData: [],
        columnConfigs: []
      }));
      
      // Sort by lastModified (most recent first), then by created
      return templates.sort((a: TemplateData, b: TemplateData) => {
        const aModified = a.lastModified || a.created || 0;
        const bModified = b.lastModified || b.created || 0;
        
        if (aModified !== bModified) {
          return bModified - aModified; // Descending order (newest first)
        }
        
        const aCreated = a.created || 0;
        const bCreated = b.created || 0;
        return bCreated - aCreated; // Descending order (newest first)
      });
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error getting user templates from Firestore:', error);
    return [];
  }
};

/**
 * Save a template to user's personal templates (optimized)
 */
export const saveUserTemplateToFirestore = async (templateData: TemplateData, userId: string): Promise<void> => {
  try {
    const userTemplatesRef = doc(db, COLLECTIONS.USER_TEMPLATES, userId);
    
    // Create metadata for the templates list
    const templateMetadata = {
      id: templateData.id,
      title: templateData.title,
      description: templateData.description || '',
      category: templateData.category || 'Personal',
      isPublic: false,
      created: templateData.created || Date.now(),
      lastModified: Date.now(),
      userId: userId
    };

    // Convert gridData array to map format for storage
    const gridDataMap = convertGridDataToMap(templateData.gridData);

    // Save full template data separately for efficient retrieval
    const templateDataRef = doc(db, COLLECTIONS.TEMPLATE_DATA, templateData.id);
    const cleanedTemplateData = cleanDataForFirestore({
      ...templateData,
      gridData: gridDataMap
    });
    await setDoc(templateDataRef, {
      ...cleanedTemplateData,
      lastModified: Date.now(),
      created: cleanedTemplateData.created || Date.now(),
      userId: userId
    });
    
    // Get current user templates metadata
    const userTemplatesSnap = await getDoc(userTemplatesRef);
    let templates: any[] = [];
    
    if (userTemplatesSnap.exists()) {
      const data = userTemplatesSnap.data();
      templates = data.templates || [];
    }
    
    // Add the new template metadata
    templates.push(templateMetadata);
    
    // Save back to Firestore (only metadata)
    await setDoc(userTemplatesRef, {
      userId: userId,
      templates: templates,
      lastUpdated: serverTimestamp(),
      created: userTemplatesSnap.exists() ? userTemplatesSnap.data().created : serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error saving user template to Firestore:', error);
    throw error;
  }
};

/**
 * Delete a template from user's personal templates (optimized)
 */
export const deleteUserTemplateFromFirestore = async (templateId: string, userId: string): Promise<void> => {
  try {
    // Delete the full template data
    const templateDataRef = doc(db, COLLECTIONS.TEMPLATE_DATA, templateId);
    await deleteDoc(templateDataRef);

    // Remove from user templates metadata
    const userTemplatesRef = doc(db, COLLECTIONS.USER_TEMPLATES, userId);
    const userTemplatesSnap = await getDoc(userTemplatesRef);
    
    if (!userTemplatesSnap.exists()) {
      throw new Error('User templates document not found');
    }
    
    const data = userTemplatesSnap.data();
    const templates = data.templates || [];
    
    // Remove the template metadata
    const updatedTemplates = templates.filter((template: any) => template.id !== templateId);
    
    // Save back to Firestore
    await updateDoc(userTemplatesRef, {
      templates: updatedTemplates,
      lastUpdated: serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error deleting user template from Firestore:', error);
    throw error;
  }
};

/**
 * Get template preview data (optimized - loads only needed data)
 */
export const getTemplatePreviewFromFirestore = async (templateId: string, type: 'general' | 'personal', userId?: string): Promise<GridData | null> => {
  try {
    // For preview, we only need limited data from the full template
    const templateDataRef = doc(db, COLLECTIONS.TEMPLATE_DATA, templateId);
    const templateDataSnap = await getDoc(templateDataRef);
    
    if (!templateDataSnap.exists()) {
      console.warn(`Template data not found for ${templateId}, falling back to array search`);
      // Fallback to old method for existing templates
      return getTemplatePreviewFromFirestoreLegacy(templateId, type, userId);
    }
    
    const template = templateDataSnap.data() as TemplateData;
    
    // Convert gridData from map back to array format
    const fullGridDataArray = convertGridDataToArray(template.gridData || {});
    const fullColumnConfigs = template.columnConfigs || [];
    
    // Limit to first 8 rows and 5 columns for preview
    const previewGridData = fullGridDataArray.slice(0, 8).map((row: any) => {
      const previewRow: any = { id: row.id };
      
      // Only include data for first 5 columns
      fullColumnConfigs.slice(0, 5).forEach((col: any) => {
        previewRow[col.id] = row[col.id] || '';
      });
      
      return previewRow;
    });
    
    return {
      id: template.id,
      title: template.title,
      gridData: previewGridData,
      columnConfigs: fullColumnConfigs.slice(0, 5), // Only first 5 columns
      created: template.created,
      lastModified: template.lastModified,
      userId: template.userId
    };
  } catch (error) {
    console.error(`Error getting template preview ${templateId}:`, error);
    return null;
  }
};

/**
 * Legacy fallback for template preview (for existing templates stored in arrays)
 */
const getTemplatePreviewFromFirestoreLegacy = async (templateId: string, type: 'general' | 'personal', userId?: string): Promise<GridData | null> => {
  try {
    let templates: TemplateData[] = [];
    
    if (type === 'general') {
      const templatesRef = doc(db, COLLECTIONS.TEMPLATES, 'public');
      const templatesSnap = await getDoc(templatesRef);
      if (templatesSnap.exists()) {
        const data = templatesSnap.data();
        templates = data.templates || [];
      }
    } else if (type === 'personal' && userId) {
      const userTemplatesRef = doc(db, COLLECTIONS.USER_TEMPLATES, userId);
      const userTemplatesSnap = await getDoc(userTemplatesRef);
      if (userTemplatesSnap.exists()) {
        const data = userTemplatesSnap.data();
        templates = data.templates || [];
      }
    }
    
    const template = templates.find(t => t.id === templateId);
    
    if (!template) {
      return null;
    }
    
    // Convert gridData from array or map to array format
    const fullGridDataArray = Array.isArray(template.gridData) 
      ? template.gridData 
      : convertGridDataToArray(template.gridData || {});
    const fullColumnConfigs = template.columnConfigs || [];
    
    // Limit to first 8 rows and 5 columns for preview
    const previewGridData = fullGridDataArray.slice(0, 8).map((row: any) => {
      const previewRow: any = { id: row.id };
      
      // Only include data for first 5 columns
      fullColumnConfigs.slice(0, 5).forEach((col: any) => {
        previewRow[col.id] = row[col.id] || '';
      });
      
      return previewRow;
    });
    
    return {
      id: template.id,
      title: template.title,
      gridData: previewGridData,
      columnConfigs: fullColumnConfigs.slice(0, 5), // Only first 5 columns
      created: template.created,
      lastModified: template.lastModified,
      userId: template.userId
    };
  } catch (error) {
    console.error(`Error getting legacy template preview ${templateId}:`, error);
    return null;
  }
};

/**
 * Get full template data for creating a new grid (optimized)
 */
export const getFullTemplateFromFirestore = async (templateId: string, type: 'general' | 'personal', userId?: string): Promise<TemplateData | null> => {
  try {
    // Try to get from the new individual template data collection first
    const templateDataRef = doc(db, COLLECTIONS.TEMPLATE_DATA, templateId);
    const templateDataSnap = await getDoc(templateDataRef);
    
    if (templateDataSnap.exists()) {
      const template = templateDataSnap.data() as TemplateData;
      
      // Convert gridData from map back to array format
      const gridDataArray = convertGridDataToArray(template.gridData || {});
      
      return {
        ...template,
        gridData: gridDataArray
      };
    }
    
    // Fallback to old method for existing templates
    console.warn(`Template data not found in templateData collection for ${templateId}, falling back to array search`);
    let templates: TemplateData[] = [];
    
    if (type === 'general') {
      const templatesRef = doc(db, COLLECTIONS.TEMPLATES, 'public');
      const templatesSnap = await getDoc(templatesRef);
      if (templatesSnap.exists()) {
        const data = templatesSnap.data();
        templates = data.templates || [];
      }
    } else if (type === 'personal' && userId) {
      const userTemplatesRef = doc(db, COLLECTIONS.USER_TEMPLATES, userId);
      const userTemplatesSnap = await getDoc(userTemplatesRef);
      if (userTemplatesSnap.exists()) {
        const data = userTemplatesSnap.data();
        templates = data.templates || [];
      }
    }
    
    const template = templates.find(t => t.id === templateId);
    if (!template) {
      return null;
    }
    
    // Ensure gridData is in array format (backward compatibility)
    const gridDataArray = Array.isArray(template.gridData) 
      ? template.gridData 
      : convertGridDataToArray(template.gridData || {});
    
    return {
      ...template,
      gridData: gridDataArray
    };
  } catch (error) {
    console.error(`Error getting full template ${templateId}:`, error);
    return null;
  }
};

/**
 * Save a template to global templates (optimized - for testing purposes)
 */
export const saveGlobalTemplateToFirestore = async (templateData: TemplateData): Promise<void> => {
  try {
    // Create metadata for the templates list
    const templateMetadata = {
      id: templateData.id,
      title: templateData.title,
      description: templateData.description || '',
      category: templateData.category || 'General',
      isPublic: true,
      created: templateData.created || Date.now(),
      lastModified: Date.now(),
      userId: templateData.userId
    };

    // Convert gridData array to map format for storage
    const gridDataMap = convertGridDataToMap(templateData.gridData);

    // Save full template data separately
    const templateDataRef = doc(db, COLLECTIONS.TEMPLATE_DATA, templateData.id);
    const cleanedTemplateData = cleanDataForFirestore({
      ...templateData,
      gridData: gridDataMap
    });
    await setDoc(templateDataRef, {
      ...cleanedTemplateData,
      lastModified: Date.now(),
      created: cleanedTemplateData.created || Date.now(),
      isPublic: true,
      category: templateData.category || 'General'
    });

    // Get current global templates metadata
    const globalTemplatesRef = doc(db, COLLECTIONS.TEMPLATES, 'public');
    const globalTemplatesSnap = await getDoc(globalTemplatesRef);
    let templates: any[] = [];
    
    if (globalTemplatesSnap.exists()) {
      const data = globalTemplatesSnap.data();
      templates = data.templates || [];
    }
    
    // Add the new template metadata
    templates.push(templateMetadata);
    
    // Save back to Firestore (only metadata)
    await setDoc(globalTemplatesRef, {
      templates: templates,
      lastUpdated: serverTimestamp(),
      created: globalTemplatesSnap.exists() ? globalTemplatesSnap.data().created : serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error saving global template to Firestore:', error);
    throw error;
  }
};

/**
 * Delete a template from global templates (for testing purposes)
 */
export const deleteGlobalTemplateFromFirestore = async (templateId: string): Promise<void> => {
  try {
    // Delete the full template data
    const templateDataRef = doc(db, COLLECTIONS.TEMPLATE_DATA, templateId);
    await deleteDoc(templateDataRef);

    // Remove from global templates metadata
    const globalTemplatesRef = doc(db, COLLECTIONS.TEMPLATES, 'public');
    const globalTemplatesSnap = await getDoc(globalTemplatesRef);
    
    if (!globalTemplatesSnap.exists()) {
      throw new Error('Global templates document not found');
    }
    
    const data = globalTemplatesSnap.data();
    const templates = data.templates || [];
    
    // Remove the template metadata
    const updatedTemplates = templates.filter((template: any) => template.id !== templateId);
    
    // Save back to Firestore
    await updateDoc(globalTemplatesRef, {
      templates: updatedTemplates,
      lastUpdated: serverTimestamp()
    });
    
  } catch (error) {
    console.error('Error deleting global template from Firestore:', error);
    throw error;
  }
};

/**
 * Get user subscription data from Firestore
 */
export const getUserSubscriptionFromFirestore = async (userId: string): Promise<SubscriptionData | null> => {
  try {
    const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, userId);
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (subscriptionSnap.exists()) {
      const data = subscriptionSnap.data();
      return {
        userId: data.userId,
        plan: data.plan,
        credits: data.credits,
        maxCredits: data.maxCredits,
        creditsResetDate: data.creditsResetDate,
        isActive: data.isActive,
        createdAt: data.createdAt?.toMillis ? data.createdAt.toMillis() : data.createdAt,
        updatedAt: data.updatedAt?.toMillis ? data.updatedAt.toMillis() : data.updatedAt,
        billingCycle: data.billingCycle,
        subscriptionId: data.subscriptionId,
      };
    } else {
      console.warn(`No subscription found for user ${userId}`);
      return null;
    }
  } catch (error) {
    console.error('Error getting user subscription from Firestore:', error);
    throw error;
  }
};

/**
 * Deduct credits from user's subscription balance
 */
export const deductCreditsFromSubscription = async (userId: string, creditsToDeduct: number): Promise<boolean> => {
  try {
    const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, userId);
    
    // Get current subscription data
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    if (!subscriptionSnap.exists()) {
      console.error(`No subscription found for user ${userId}`);
      return false;
    }
    
    const currentData = subscriptionSnap.data();
    const currentCredits = currentData.credits || 0;
    
    // Check if there are enough credits
    if (currentCredits < creditsToDeduct) {
      console.error(`Insufficient credits: trying to deduct ${creditsToDeduct} but only ${currentCredits} available`);
      return false;
    }
    
    // Update the credits
    const newCredits = currentCredits - creditsToDeduct;
    
    await updateDoc(subscriptionRef, {
      credits: newCredits,
      updatedAt: serverTimestamp(),
    });
    
    return true;
    
  } catch (error) {
    console.error('Error deducting credits from subscription:', error);
    return false;
  }
};

/**
 * Save only changed cells to Firestore (optimized for large grids)
 */
export const saveCellChangesToFirestore = async (
  gridId: string, 
  userId: string, 
  cellChanges: CellChange[]
): Promise<void> => {
  try {
    if (!userId || userId.trim() === '') {
      throw new Error('Invalid userId: userId is required and cannot be empty');
    }

    if (cellChanges.length === 0) {
      return; // No changes to save
    }

    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    const gridRef = doc(userDocRef, 'grids', gridId);
    
    // Group changes by row for efficient updates
    const rowUpdates: Record<string, Record<string, any>> = {};
    
    cellChanges.forEach(change => {
      const rowKey = change.rowIndex.toString();
      if (!rowUpdates[rowKey]) {
        rowUpdates[rowKey] = {};
      }
      rowUpdates[rowKey][change.columnId] = change.newValue;
    });
    
    // Build the update object for Firestore
    const updateData: Record<string, any> = {
      lastModified: serverTimestamp()
    };
    
    // Add each cell update to the update object
    Object.keys(rowUpdates).forEach(rowIndex => {
      Object.keys(rowUpdates[rowIndex]).forEach(columnId => {
        const fieldPath = `gridData.${rowIndex}.${columnId}`;
        updateData[fieldPath] = rowUpdates[rowIndex][columnId];
      });
    });
    
    // Update only the changed fields
    await updateDoc(gridRef, updateData);
    
    // Update metadata with new lastModified time
    await updateGridMetadataLastModified(gridId, userId);
    
  } catch (error) {
    console.error('Error saving cell changes to Firestore:', error);
    throw error;
  }
};

/**
 * Save row-level changes to Firestore (for row insertions, deletions, reordering)
 */
export const saveRowChangesToFirestore = async (
  gridId: string,
  userId: string,
  rowChanges: RowChange[]
): Promise<void> => {
  try {
    if (!userId || userId.trim() === '') {
      throw new Error('Invalid userId: userId is required and cannot be empty');
    }

    if (rowChanges.length === 0) {
      return; // No changes to save
    }

    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    const gridRef = doc(userDocRef, 'grids', gridId);
    
    // For row changes, we need to rebuild the affected parts of the gridData map
    const updateData: Record<string, any> = {
      lastModified: serverTimestamp()
    };
    
    rowChanges.forEach(change => {
      const rowKey = change.rowIndex.toString();
      
      if (change.changeType === 'delete') {
        // For deletions, set the row to null (Firestore will remove the field)
        updateData[`gridData.${rowKey}`] = null;
      } else {
        // For updates and inserts, set the entire row
        updateData[`gridData.${rowKey}`] = cleanDataForFirestore(change.row);
      }
    });
    
    // Update the document
    await updateDoc(gridRef, updateData);
    
    // Update metadata with new lastModified time
    await updateGridMetadataLastModified(gridId, userId);
    
  } catch (error) {
    console.error('Error saving row changes to Firestore:', error);
    throw error;
  }
};

/**
 * Batch multiple types of changes together for optimal performance
 */
export const saveBatchedChangesToFirestore = async (
  gridId: string,
  userId: string,
  cellChanges: CellChange[] = [],
  rowChanges: RowChange[] = [],
  columnConfigChanges?: any[]
): Promise<void> => {
  try {
    if (!userId || userId.trim() === '') {
      throw new Error('Invalid userId: userId is required and cannot be empty');
    }

    const hasChanges = cellChanges.length > 0 || rowChanges.length > 0 || (columnConfigChanges && columnConfigChanges.length > 0);
    
    if (!hasChanges) {
      return; // No changes to save
    }

    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    const gridRef = doc(userDocRef, 'grids', gridId);
    
    const updateData: Record<string, any> = {
      lastModified: serverTimestamp()
    };
    
    // Process cell changes
    if (cellChanges.length > 0) {
      const rowUpdates: Record<string, Record<string, any>> = {};
      
      cellChanges.forEach(change => {
        const rowKey = change.rowIndex.toString();
        if (!rowUpdates[rowKey]) {
          rowUpdates[rowKey] = {};
        }
        rowUpdates[rowKey][change.columnId] = change.newValue;
      });
      
      // Add cell updates to the update object
      Object.keys(rowUpdates).forEach(rowIndex => {
        Object.keys(rowUpdates[rowIndex]).forEach(columnId => {
          const fieldPath = `gridData.${rowIndex}.${columnId}`;
          updateData[fieldPath] = rowUpdates[rowIndex][columnId];
        });
      });
    }
    
    // Process row changes
    if (rowChanges.length > 0) {
      rowChanges.forEach(change => {
        const rowKey = change.rowIndex.toString();
        
        if (change.changeType === 'delete') {
          updateData[`gridData.${rowKey}`] = null;
        } else {
          updateData[`gridData.${rowKey}`] = cleanDataForFirestore(change.row);
        }
      });
    }
    
    // Process column config changes
    if (columnConfigChanges && columnConfigChanges.length > 0) {
      updateData.columnConfigs = cleanDataForFirestore(columnConfigChanges);
    }
    
    // Apply all updates in a single operation
    await updateDoc(gridRef, updateData);
    
    // Update metadata with new lastModified time
    await updateGridMetadataLastModified(gridId, userId);
    
  } catch (error) {
    console.error('Error saving batched changes to Firestore:', error);
    throw error;
  }
};

/**
 * Update only the lastModified timestamp in grid metadata
 */
const updateGridMetadataLastModified = async (gridId: string, userId: string): Promise<void> => {
  try {
    const userDocRef = doc(db, COLLECTIONS.GRIDS, userId);
    const userDocSnap = await getDoc(userDocRef);
    
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      const grids = userData.grids || [];
      
      // Find and update the grid metadata
      const existingIndex = grids.findIndex((g: any) => g.id === gridId);
      
      if (existingIndex >= 0) {
        grids[existingIndex].lastModified = Date.now();
        
        await updateDoc(userDocRef, {
          grids: grids,
          lastUpdated: serverTimestamp()
        });
      }
    }
  } catch (error) {
    console.error(`Error updating grid metadata for ${gridId}:`, error);
    // Don't throw here as this is a secondary operation
  }
};

/**
 * Track and apply incremental changes to a grid (higher-level helper)
 */
export const applyIncrementalGridChanges = async (
  gridId: string,
  userId: string,
  changes: {
    cellChanges?: CellChange[];
    rowChanges?: RowChange[];
    columnConfigChanges?: any[];
    title?: string;
  }
): Promise<void> => {
  try {
    // Handle title change separately if provided
    if (changes.title !== undefined) {
      await updateGridTitle(gridId, userId, changes.title);
    }
    
    // Apply other changes in batch
    const { cellChanges = [], rowChanges = [], columnConfigChanges } = changes;
    
    await saveBatchedChangesToFirestore(
      gridId,
      userId,
      cellChanges,
      rowChanges,
      columnConfigChanges
    );
  } catch (error) {
    console.error('Error applying incremental grid changes:', error);
    throw error;
  }
};

/**
 * Helper function to create cell change objects
 */
export const createCellChange = (
  rowIndex: number,
  columnId: string,
  newValue: any,
  oldValue?: any
): CellChange => ({
  rowIndex,
  columnId,
  newValue,
  oldValue
});

/**
 * Helper function to create row change objects
 */
export const createRowChange = (
  rowIndex: number,
  row: any,
  changeType: 'update' | 'insert' | 'delete'
): RowChange => ({
  rowIndex,
  row,
  changeType
});

/**
 * Plan configuration - maps plan names to credit amounts
 */
const PLAN_CREDITS: Record<string, number> = {
  'free_user': 30,
  '2k_credits': 2000,
  '10k_credits': 10000,
  '20k_credits': 20000,
};

/**
 * Get user's current plan from Clerk using auth object
 */
export const getUserPlanFromClerk = async (authObj: any): Promise<string> => {
  try {

    if (!authObj) {
      return 'free_user';
    }

    // Method 1: Try using the has() method if available (this should be the correct way)
    if (typeof authObj.has === 'function') {
      
      // Check for each plan using Clerk's has() method
      if (authObj.has({ plan: '20k_credits' })) {
        return '20k_credits';
      }
      if (authObj.has({ plan: '10k_credits' })) {
        return '10k_credits';
      }
      if (authObj.has({ plan: '2k_credits' })) {
        return '2k_credits';
      }
      
    }

    // Method 2: Check if auth object has user metadata access
    const user = authObj.user;
    if (user) {
      
      const metadata = user.publicMetadata || user.privateMetadata || user.unsafeMetadata || {};
      
      if (metadata.plan) {
        return metadata.plan;
      }
      
      if (metadata.subscription) {
        return metadata.subscription.plan || metadata.subscription;
      }
    }

    // Method 3: Check for any subscription-related properties on auth object
    const subscriptionProps = ['subscriptions', 'billing', 'plan', 'currentPlan'];
    for (const prop of subscriptionProps) {
      if (authObj[prop]) {
        
        // If it's an array, check the first active subscription
        if (Array.isArray(authObj[prop])) {
          const activeSubscription = authObj[prop].find((sub: any) => sub.status === 'active' || sub.active);
          if (activeSubscription && activeSubscription.plan) {
            return activeSubscription.plan;
          }
        }
        
        // If it's an object with plan property
        if (typeof authObj[prop] === 'object' && authObj[prop].plan) {
          return authObj[prop].plan;
        }
        
        // If it's directly a string (plan name)
        if (typeof authObj[prop] === 'string') {
          return authObj[prop];
        }
      }
    }

    return 'free_user';
  } catch (error) {
    console.error('❌ Error getting user plan from Clerk:', error);
    return 'free_user'; // Default to free user on error
  }
};

/**
 * Save or restore free user balance when upgrading/downgrading
 */
export const saveOrRestoreFreeUserBalance = async (
  userId: string,
  currentSubscription: SubscriptionData,
  newPlan: string,
  action: 'save' | 'restore'
): Promise<number> => {
  try {
    const userSettingsRef = doc(db, COLLECTIONS.USER_SETTINGS, userId);
    
    if (action === 'save' && currentSubscription.plan === 'free_user') {
      // Save current free user balance before upgrading
      await setDoc(userSettingsRef, {
        savedFreeUserCredits: currentSubscription.credits,
        savedFreeUserMaxCredits: currentSubscription.maxCredits,
        lastSavedAt: serverTimestamp()
      }, { merge: true });
      
      return currentSubscription.credits;
    } else if (action === 'restore' && newPlan === 'free_user') {
      // Restore previous free user balance when downgrading
      const userSettingsSnap = await getDoc(userSettingsRef);
      
      if (userSettingsSnap.exists()) {
        const data = userSettingsSnap.data();
        const savedCredits = data.savedFreeUserCredits || PLAN_CREDITS['free_user'];
        
        return savedCredits;
      }
    }
    
    // Return default credits for the plan
    return PLAN_CREDITS[newPlan] || PLAN_CREDITS['free_user'];
  } catch (error) {
    console.error('Error saving/restoring free user balance:', error);
    return PLAN_CREDITS[newPlan] || PLAN_CREDITS['free_user'];
  }
};

/**
 * Update subscription data in Firestore based on plan change
 */
export const updateSubscriptionForPlanChange = async (
  userId: string,
  newPlan: string,
  currentSubscription: SubscriptionData | null
): Promise<SubscriptionData> => {
  try {
    const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, userId);
    
    // Determine new credits based on plan
    let newCredits: number;
    let newMaxCredits: number;
    
    if (newPlan === 'free_user' && currentSubscription && currentSubscription.plan !== 'free_user') {
      // Downgrading to free - restore previous free user balance
      newCredits = await saveOrRestoreFreeUserBalance(userId, currentSubscription, newPlan, 'restore');
      newMaxCredits = PLAN_CREDITS['free_user'];
    } else if (newPlan !== 'free_user' && currentSubscription && currentSubscription.plan === 'free_user') {
      // Upgrading from free - save current balance and set new plan credits
      await saveOrRestoreFreeUserBalance(userId, currentSubscription, newPlan, 'save');
      newCredits = PLAN_CREDITS[newPlan] || PLAN_CREDITS['free_user'];
      newMaxCredits = newCredits;
    } else {
      // Plan change between paid plans or new user
      newCredits = PLAN_CREDITS[newPlan] || PLAN_CREDITS['free_user'];
      newMaxCredits = newCredits;
    }
    
    // Calculate credits reset date for paid plans (monthly reset)
    let creditsResetDate: number | null = null;
    if (newPlan !== 'free_user') {
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      nextMonth.setDate(1); // First day of next month
      nextMonth.setHours(0, 0, 0, 0);
      creditsResetDate = nextMonth.getTime();
    }
    
    // Prepare subscription data
    const updatedSubscription: SubscriptionData = {
      userId,
      plan: newPlan,
      credits: newCredits,
      maxCredits: newMaxCredits,
      creditsResetDate,
      isActive: true,
      createdAt: currentSubscription?.createdAt || Date.now(),
      updatedAt: Date.now(),
      billingCycle: newPlan === 'free_user' ? null : 'monthly',
      subscriptionId: currentSubscription?.subscriptionId || null,
    };
    
    // Update Firestore
    await setDoc(subscriptionRef, {
      ...updatedSubscription,
      createdAt: currentSubscription?.createdAt ? Timestamp.fromMillis(currentSubscription.createdAt) : serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return updatedSubscription;
  } catch (error) {
    console.error('Error updating subscription for plan change:', error);
    throw error;
  }
};

/**
 * Sync user's plan between Clerk and Firestore
 * This should be called whenever you want to check if the user's plan has changed
 */
export const syncUserPlanWithClerk = async (
  userId: string,
  authObj: any
): Promise<{ updated: boolean; oldPlan?: string; newPlan?: string; subscription: SubscriptionData }> => {
  try {
    // Get current plan from Clerk
    const clerkPlan = await getUserPlanFromClerk(authObj);
    
    // Get current subscription from Firestore
    const currentSubscription = await getUserSubscriptionFromFirestore(userId);
    
    // If no current subscription exists, create one with the Clerk plan
    if (!currentSubscription) {
      const newSubscription = await updateSubscriptionForPlanChange(userId, clerkPlan, null);
      return {
        updated: true,
        newPlan: clerkPlan,
        subscription: newSubscription
      };
    }
    
    // Check if plans differ
    if (currentSubscription.plan !== clerkPlan) {
      
      const updatedSubscription = await updateSubscriptionForPlanChange(userId, clerkPlan, currentSubscription);
      
      return {
        updated: true,
        oldPlan: currentSubscription.plan,
        newPlan: clerkPlan,
        subscription: updatedSubscription
      };
    }
    
    // Plans are in sync
    return {
      updated: false,
      subscription: currentSubscription
    };
  } catch (error) {
    console.error('Error syncing user plan with Clerk:', error);
    throw error;
  }
};

/**
 * Check and sync user plan with Clerk - convenience function for React components
 * This function can be called from React components that have access to Clerk auth
 */
export const checkAndSyncUserPlan = async (
  userId: string,
  authObj: any,
  onPlanUpdated?: (oldPlan: string, newPlan: string) => void
): Promise<SubscriptionData> => {
  try {
    const syncResult = await syncUserPlanWithClerk(userId, authObj);
    
    if (syncResult.updated && onPlanUpdated && syncResult.oldPlan && syncResult.newPlan) {
      onPlanUpdated(syncResult.oldPlan, syncResult.newPlan);
    }
    
    return syncResult.subscription;
  } catch (error) {
    console.error('Error checking and syncing user plan:', error);
    throw error;
  }
};

/**
 * Reset monthly credits for paid plan users
 * This function should be called monthly (can be triggered by a Cloud Function or cron job)
 */
export const resetMonthlyCreditsForUser = async (userId: string): Promise<void> => {
  try {
    const subscription = await getUserSubscriptionFromFirestore(userId);
    
    if (!subscription || subscription.plan === 'free_user') {
      return; // Free users don't get monthly resets
    }
    
    const now = Date.now();
    
    // Check if it's time to reset credits
    if (subscription.creditsResetDate && now >= subscription.creditsResetDate) {
      const maxCredits = PLAN_CREDITS[subscription.plan] || subscription.maxCredits;
      
      // Calculate next reset date
      const nextResetDate = new Date(subscription.creditsResetDate);
      nextResetDate.setMonth(nextResetDate.getMonth() + 1);
      
      // Update subscription with reset credits
      const subscriptionRef = doc(db, COLLECTIONS.SUBSCRIPTIONS, userId);
      await updateDoc(subscriptionRef, {
        credits: maxCredits,
        creditsResetDate: nextResetDate.getTime(),
        updatedAt: serverTimestamp(),
      });
      
    }
  } catch (error) {
    console.error('Error resetting monthly credits:', error);
    throw error;
  }
}; 